import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Column } from "primereact/column";
import Spinner from "../../components/spinner";
import { axiosApi } from "../../../services/axios_api";
import PaginatorComponent from "../../components/paginator";
import useDebounce from "../../components/hooks/useDebounce";
import { toast } from "react-toastify";
import { Tag } from "primereact/tag";
import moment from "moment";
import { exportToExcel, renderTextWithTooltip } from "../../components/helper";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import Dots from "../../../../assets/assests/detail.png";
import Details from "./details-queue";
import { JsonToTable } from "react-json-to-table";

export default function QueueManagementScreen({ }) {
  const [loading, setLoading] = useState(false);
  const [filteredList, setFilteredList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [fromDate, setFromDate] = useState(moment().startOf("month").toDate());
  const [toDate, setToDate] = useState(moment().endOf("month").toDate());
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowselect, setRowselect] = useState(null);
  const [showdetail, setShowdetail] = useState(false);
  const debouncedSearch = useDebounce(globalFilter, 500);
  const [responseDialogVisible, setResponseDialogVisible] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);

  const getLogsData = async (pageNumber = 1, pageSize = rows) => {
    setLoading(true);
    try {
      const response = await axiosApi.get(
        `Portal/activity-logs/get-all-paginated`,
        {
          params: {
            pageNumber,
            pageSize,
            searchTerm: debouncedSearch || "",
            fromDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : null,
            toDate: toDate ? moment(toDate).format("YYYY-MM-DD") : null,
          },
        }
      );
      setFilteredList(response.data.body);
      setTotalRecords(response.data.totalRecords);
    } catch (error) {
      toast.error("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event) => {
    const { first, rows } = event;
    setFirst(first);
    setRows(rows);
    const pageNumber = first / rows + 1;
    getLogsData(pageNumber, rows);
  };

  useEffect(() => {
    if (debouncedSearch) {
      getLogsData(1, rows, debouncedSearch);
    } else {
      getLogsData(1, rows);
    }
  }, [debouncedSearch, fromDate, toDate]);
  const actionTemplate = (rowData) => {
    const isActive = rowData?.isActive;
    return (
      <div className="action-buttons">
        <Button
          className="p-button-edit p-0 mr-3"
          onClick={() => showDetails(rowData)}
        >
          <img src={Dots} alt="Details" />
        </Button>
      </div>
    );
  };
  const showDetails = (rowData) => {
    setRowselect(rowData);
    setShowdetail(true);
  };

  const handleViewResponse = (rowData) => {
    // Helper: convert an XML string to a JSON-like object using DOMParser
    const parseXmlStringToJson = (xmlString) => {
      try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, "application/xml");

        // detect parse error
        if (xmlDoc.getElementsByTagName("parsererror").length) return null;

        const nodeToObject = (node) => {
          // text node or element with no child elements
          const children = Array.from(node.children || []);
          const hasChildElements = children.length > 0;

          // collect attributes
          const obj = {};
          if (node.attributes && node.attributes.length) {
            Array.from(node.attributes).forEach((attr) => {
              obj[`@${attr.name}`] = attr.value;
            });
          }

          if (!hasChildElements) {
            const text = (node.textContent || "").trim();
            if (Object.keys(obj).length) {
              if (text) obj["#text"] = text;
              return obj;
            }
            return text;
          }

          children.forEach((child) => {
            const name = child.nodeName;
            const childObj = nodeToObject(child);
            if (obj[name] !== undefined) {
              // convert to array if multiple siblings with same name
              if (!Array.isArray(obj[name])) obj[name] = [obj[name]];
              obj[name].push(childObj);
            } else {
              obj[name] = childObj;
            }
          });

          return obj;
        };

        const root = xmlDoc.documentElement;
        return { [root.nodeName]: nodeToObject(root) };
      } catch (e) {
        console.error("XML parse error", e);
        return null;
      }
    };

    try {
      // console.log("Raw Response String:", rowData.response);

      let raw = rowData.response;

      if (typeof raw === "string" && raw.startsWith('"') && raw.endsWith('"')) {
        try {
          raw = JSON.parse(raw);
        } catch (e) {
          raw = raw.slice(1, -1);
        }
      }

      let parsedResponse = null;

      if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (trimmed.startsWith("<") || trimmed.startsWith("<?xml") || /<\w+:/i.test(trimmed)) {
          parsedResponse = parseXmlStringToJson(trimmed) || trimmed;
        } else {
          try {
            parsedResponse = trimmed ? JSON.parse(trimmed) : null;
          } catch (e) {
            parsedResponse = trimmed;
          }
        }
      } else {
        parsedResponse = raw;
      }

      // console.log("Parsed Response Object:", parsedResponse);
      setSelectedResponse(parsedResponse);
    } catch (error) {
      console.error("Parsing Error", error);
      setSelectedResponse(null);
    }

    setResponseDialogVisible(true);
  };

  const columnsData = [
    {
      field: "id",
      header: "ID",
      body: (rowData) => rowData.id || "--",
      frozen: true,
    },
    {
      field: "kioskId",
      header: "Kiosk Id",
      body: (rowData) => rowData.kioskId || "--",
    },
    {
      field: "requestType",
      header: "Request Type",
      body: (rowData) =>
        renderTextWithTooltip(rowData, "requestType", "bottom"),
    },
    {
      field: "machinID",
      header: "Machine ID",
      body: (rowData) => rowData.machinID || "--",
    },
    {
      field: "createdDate",
      header: "Created Date",
      body: (rowData) =>
        rowData.createdDate
          ? new Date(rowData.createdDate).toLocaleString()
          : "--",
    },
    {
      field: "startTime",
      header: "Start Time",
      body: (rowData) =>
        rowData.startTime ? new Date(rowData.startTime).toLocaleString() : "--",
    },
    {
      field: "endTime",
      header: "End Time",
      body: (rowData) =>
        rowData.endTime ? new Date(rowData.endTime).toLocaleString() : "--",
    },
    {
      field: "totalProcessingTime",
      header: "Processing Time (s)",
      body: (rowData) => rowData.totalProcessingTime || "--",
    },
    {
      field: "status",
      header: "Status",
      body: (rowData) => {
        const statusText = rowData?.status ? String(rowData.status) : "";
        const isSuccess = /success/i.test(statusText);
        return statusText && statusText !== "--" ? (
          <Tag
            className={isSuccess ? "custom-success-tag" : "custom-danger-tag"}
            value={isSuccess ? "Success" : statusText}
          />
        ) : (
          "--"
        );
      },
    },
    {
      header: "Response",
      body: (rowData) => (
        <Button
          label="View"
          icon="pi pi-eye"
          className="p-text p-button-sm"
          onClick={() => handleViewResponse(rowData)}
        />
      ),
    },
    {
      header: "Action",
      body: actionTemplate,
    },
  ];

  const formatKey = (key) => {
    return key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .replace(/\b([a-z])/g, (char) => char.toUpperCase())
      .trim();
  };

  const formatKeys = (obj) => {
    if (!obj || typeof obj !== "object") return obj;

    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        formatKey(key),
        Array.isArray(value) ? value.map(formatKeys) : value,
      ])
    );
  };

  const formattedResponse = formatKeys(selectedResponse);

  return (
    <>
      <Dialog
        header="Response"
        visible={responseDialogVisible}
        className="dialog-size"
        onHide={() => setResponseDialogVisible(false)}
      >
        {selectedResponse ? (
          typeof selectedResponse === "object" ? (
            <JsonToTable json={formattedResponse} />
          ) : (
            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {String(selectedResponse)}
            </pre>
          )
        ) : (
          <p className="text-center">No data available</p>
        )}
      </Dialog>

      <Dialog
        visible={showdetail}
        onHide={() => setShowdetail(false)}
        header="Queue Details"
        className="dialog-size"
      >
        <Details rowData={rowselect} />
      </Dialog>

      <div className="grid align-items-center mb-3 p-2 justify-content-between gap-3">
        <div>
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              className="mbl_view"
              placeholder="Search"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
          </span>
        </div>
        <div className="grid gap-3">
          <Calendar
            placeholder="Select From Date"
            showIcon
            value={fromDate}
            onChange={(e) => setFromDate(e.value)}
            maxDate={toDate}
          />
          <Calendar
            placeholder="Select To Date"
            showIcon
            value={toDate}
            onChange={(e) => setToDate(e.value)}
            minDate={fromDate}
          />
          {/* <Button
            className="custom-btn export-excel secondary-button"
            label="Export Excel"
            icon="pi pi-window-maximize"
            onClick={exportCSV}
          /> */}
        </div>
      </div>
      {loading && (
        <div className="spinner-overlay">
          <Spinner />
        </div>
      )}
      <div className="card card-container">
        <DataTable
          emptyMessage="No record found."
          value={filteredList}
          className="custom-data-table"
          scrollable
          globalFilter={globalFilter}
        >
          {columnsData.map((data, index) => (
            <Column
              key={index}
              field={data.field}
              header={data.header}
              body={data.body}
              frozen={data.frozen}
            />
          ))}
        </DataTable>
        {totalRecords > 0 && (
          <PaginatorComponent
            first={first}
            rows={rows}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </>
  );
}
